/**
 * GlobalHotkeysProvider — mounted once inside AppShell.
 *
 * Owns:
 *   • The `?` cheatsheet dialog state.
 *   • Every g-chord navigation shortcut (g d, g i, g o, g q, g p, g c, g s,
 *     g m, g h).
 *   • The `/` global-search-focus shortcut (dispatches a synthetic
 *     `topbar:focus-search` event that the topbar's Search button listens
 *     for — click-equivalent, opens the command palette).
 *
 * Ctrl+K continues to live inside the Topbar so it stays close to the palette
 * state it controls, but it now goes through `useHotkey` too — see topbar.tsx.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkey } from '@/shared/hooks/use-hotkey';
import { ShortcutCheatsheet } from './shortcut-cheatsheet';

/** Event fired when `/` is pressed — the topbar's search button opens on it. */
export const TOPBAR_FOCUS_SEARCH_EVENT = 'topbar:focus-search';

export function GlobalHotkeysProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  // ---- ? — cheatsheet ---------------------------------------------------- //
  // `?` is Shift+/ on US layouts — the hook is layout-tolerant on punctuation
  // and matches the character regardless of the shift state that produced it.
  useHotkey({
    keys: '?',
    handler: () => setCheatsheetOpen((v) => !v),
    description: 'Show keyboard shortcuts',
  });

  // ---- / — focus topbar search ------------------------------------------ //
  useHotkey({
    keys: '/',
    handler: () => {
      // Fire a global event — the topbar owns its own palette state.
      window.dispatchEvent(new CustomEvent(TOPBAR_FOCUS_SEARCH_EVENT));
    },
    description: 'Focus global search',
  });

  // ---- g <letter> — navigation chords ----------------------------------- //
  useHotkey({ keys: 'g d', handler: () => navigate('/dashboard') });
  useHotkey({ keys: 'g i', handler: () => navigate('/sales/invoices') });
  useHotkey({ keys: 'g o', handler: () => navigate('/billing/invoices') });
  useHotkey({ keys: 'g q', handler: () => navigate('/sites/reconciliations') });
  useHotkey({ keys: 'g p', handler: () => navigate('/products') });
  useHotkey({ keys: 'g c', handler: () => navigate('/customers') });
  useHotkey({ keys: 'g s', handler: () => navigate('/suppliers') });
  useHotkey({ keys: 'g m', handler: () => navigate('/kitchen/production') });
  useHotkey({ keys: 'g h', handler: () => navigate('/help') });

  return (
    <>
      {children}
      <ShortcutCheatsheet open={cheatsheetOpen} onOpenChange={setCheatsheetOpen} />
    </>
  );
}
